// client/src/dnd/emitter_tool/components/panels/ParticlesPanel.js
import React, { useEffect, useState } from "react";

const OPEN_KEY = "EmitterTool:ParticlesPanelOpenV1";

const labelStyle = { fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" };
const sectionHeader = { fontSize: 12, fontWeight: 600, opacity: 0.9, margin: "8px 0 6px" };
const rowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
  gap: 8,
  marginBottom: 8,
};

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }
function toNum(v, fallback = 0) { const n = Number(v); return Number.isFinite(n) ? n : fallback; }

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

function Slider({ label, value, onChange, min = 0, max = 10, step = 0.1, suffix = "" }) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : String(min));

  useEffect(() => {
    const asStr = String(Number.isFinite(value) ? value : min);
    if (asStr !== text) setText(asStr);
  }, [value, min, max]); // eslint-disable-line

  const commitNumber = (raw) => {
    const parsed = toNum(raw, value ?? min);
    const clamped = clamp(parsed, min, max);
    onChange?.(clamped);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={labelStyle}>{label}{suffix ? ` (${suffix})` : ""}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="range"
          value={Number.isFinite(value) ? value : min}
          min={min}
          max={max}
          step={step}
          onChange={(e) => { onChange?.(toNum(e.target.value, min)); }}
          style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", accentColor: "#64748b", cursor: "pointer" }}
        />
        <input
          type="number"
          value={text}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commitNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          style={{
            width: "clamp(64px, 28%, 96px)",
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "4px 6px",
            fontSize: 12,
            textAlign: "right",
            boxSizing: "border-box",
          }}
        />
        {suffix ? <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

function LabeledSelect({ label, value, onChange, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={labelStyle}>{label}</div>
      <select
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          maxWidth: "100%",
          background: "#0b0d12",
          color: "white",
          border: "1px solid #374151",
          borderRadius: 8,
          padding: "6px 8px",
          fontSize: 12,
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          cursor: "pointer",
          boxSizing: "border-box",
          display: "block",
        }}
      >
        {children}
      </select>
    </div>
  );
}

export default function ParticlesPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Particles",
  defaultOpen = true,
}) {
  const dispatchParticlesConfig = (next) => {
    try { window.dispatchEvent(new CustomEvent("EmitterTool:ParticlesConfig", { detail: next })); } catch {}
  };

  const setES = (patch) => {
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => {
        const base = cur || {};
        const next = typeof patch === "function" ? patch(base) : patch;
        const merged = { ...base, ...next };
        dispatchParticlesConfig(merged);
        return merged;
      });
    } else {
      const next = typeof patch === "function" ? patch(effectSettings || {}) : patch;
      dispatchParticlesConfig(next);
    }
  };

  const {
    pps = 100,
    maxAlive = 1000,
    speedMin = 1,
    speedMax = 3,
    lifeMinS = 1,
    lifeMaxS = 2.5,
    rangeMeters = 10,
    alphaStart = 1,
    alphaEnd = 0,
    scaleStart = 1,
    scaleEnd = 1,
    blendMode = "NORMAL",
    tintEnabled = false,
    tintStart = "#ffffff",
    tintEnd = "#ffffff",
    tintEase = "linear",
    facingMode = "deg_from_origin",
    facingDegFromOrigin = 0,
    lifespanMode = "time",
    targetKillDistMeters = 0.25,
    trailEnabled = false,
    trailMode = "ghost",
    trailLength = 8,
    trailSpacingMs = 30,
    trailAlphaStart = 0.5,
    trailAlphaEnd = 0,
    trailScaleStart = 1.0,
    trailScaleEnd = 0.2,
    ribbonWidthPx = 12,
    subTrailPps = 60,
    subTrailLifeS = 0.35,
  } = effectSettings || {};

  const setSpeedMin = (v) => setES({ speedMin: Math.min(v, speedMax) });
  const setSpeedMax = (v) => setES({ speedMax: Math.max(v, speedMin) });
  const setLifeMin  = (v) => setES({ lifeMinS: Math.min(v, lifeMaxS) });
  const setLifeMax  = (v) => setES({ lifeMaxS: Math.max(v, lifeMinS) });

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ maxWidth: "100%", overflowX: "hidden" }}>
        <div style={sectionHeader}>Throughput</div>
        <div style={rowStyle}>
          <Slider
            label="Particles / sec"
            value={pps}
            onChange={(v) => setES({ pps: v })}
            min={0}
            max={2000}
            step={1}
          />
          <Slider
            label="Max alive"
            value={maxAlive}
            onChange={(v) => setES({ maxAlive: Math.max(0, Math.floor(v)) })}
            min={0}
            max={5000}
            step={1}
          />
        </div>

        <div style={sectionHeader}>Motion</div>
        <div style={rowStyle}>
          <Slider label="Speed min" value={speedMin} onChange={setSpeedMin} min={0} max={20} step={0.1} />
          <Slider label="Speed max" value={speedMax} onChange={setSpeedMax} min={0} max={20} step={0.1} />
        </div>

        <div style={sectionHeader}>Lifespan Mode</div>
        <LabeledSelect
          label="Mode"
          value={lifespanMode}
          onChange={(e) => setES({ lifespanMode: e.target.value })}
        >
          <option value="time">Time-based (seconds)</option>
          <option value="range">Range-based (meters)</option>
          <option value="to_target">Until target is reached</option>
        </LabeledSelect>

        {lifespanMode === "time" && (
          <>
            <div style={sectionHeader}>Lifetime</div>
            <div style={rowStyle}>
              <Slider label="Lifespan min" value={lifeMinS} onChange={setLifeMin} min={0} max={60} step={0.1} suffix="s" />
              <Slider label="Lifespan max" value={lifeMaxS} onChange={setLifeMax} min={0} max={60} step={0.1} suffix="s" />
            </div>
          </>
        )}

        {lifespanMode === "range" && (
          <>
            <div style={sectionHeader}>Range</div>
            <Slider
              label="Range"
              value={rangeMeters}
              onChange={(v) => setES({ rangeMeters: v })}
              min={0}
              max={200}
              step={0.5}
              suffix="m"
            />
          </>
        )}

        {lifespanMode === "to_target" && (
          <>
            <div style={sectionHeader}>To Target</div>
            <Slider
              label="Kill distance"
              value={targetKillDistMeters}
              onChange={(v) => setES({ targetKillDistMeters: v })}
              min={0}
              max={10}
              step={0.05}
              suffix="m"
            />
            <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6, lineHeight: 1.4 }}>
              Particles stay fully opaque until they hit this threshold near the target.
            </div>
          </>
        )}

        <div style={sectionHeader}>Alpha</div>
        <div style={rowStyle}>
          <Slider
            label="Alpha start"
            value={alphaStart}
            onChange={(v) => setES({ alphaStart: Math.max(0, Math.min(1, v)) })}
            min={0}
            max={1}
            step={0.01}
          />
          <Slider
            label="Alpha end"
            value={alphaEnd}
            onChange={(v) => setES({ alphaEnd: Math.max(0, Math.min(1, v)) })}
            min={0}
            max={1}
            step={0.01}
          />
        </div>

        <div style={sectionHeader}>Scale</div>
        <div style={rowStyle}>
          <Slider
            label="Scale start"
            value={scaleStart}
            onChange={(v) => setES({ scaleStart: v })}
            min={0}
            max={5}
            step={0.05}
            suffix="×"
          />
          <Slider
            label="Scale end"
            value={scaleEnd}
            onChange={(v) => setES({ scaleEnd: v })}
            min={0}
            max={5}
            step={0.05}
            suffix="×"
          />
        </div>

        <div style={sectionHeader}>Blending</div>
        <LabeledSelect
          label="Blend mode"
          value={blendMode}
          onChange={(e) => setES({ blendMode: e.target.value })}
        >
          <option value="NORMAL">NORMAL</option>
          <option value="ADD">ADD</option>
          <option value="MULTIPLY">MULTIPLY</option>
          <option value="SCREEN">SCREEN</option>
        </LabeledSelect>

        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #334155" }}>
          <div style={sectionHeader}>Tint (optional)</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={!!tintEnabled}
              onChange={(e) => setES({ tintEnabled: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Enable tint</span>
          </label>

          <div style={rowStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", gap: 8 }}>
              <div style={labelStyle}>Start</div>
              <input
                type="color"
                value={tintStart}
                onChange={(e) => setES({ tintStart: e.target.value })}
                disabled={!tintEnabled}
                style={{ width: 40, height: 24, borderRadius: 4, border: "1px solid #374151", background: "#0b0d12" }}
              />
              <input
                type="text"
                value={tintStart}
                onChange={(e) => setES({ tintStart: e.target.value })}
                disabled={!tintEnabled}
                style={{ width: 90, background: "#0b0d12", color: "white", border: "1px solid #374151", borderRadius: 6, padding: "4px 6px", fontSize: 12 }}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", gap: 8 }}>
              <div style={labelStyle}>End</div>
              <input
                type="color"
                value={tintEnd}
                onChange={(e) => setES({ tintEnd: e.target.value })}
                disabled={!tintEnabled}
                style={{ width: 40, height: 24, borderRadius: 4, border: "1px solid #374151", background: "#0b0d12" }}
              />
              <input
                type="text"
                value={tintEnd}
                onChange={(e) => setES({ tintEnd: e.target.value })}
                disabled={!tintEnabled}
                style={{ width: 90, background: "#0b0d12", color: "white", border: "1px solid #374151", borderRadius: 6, padding: "4px 6px", fontSize: 12 }}
              />
            </div>
          </div>

          <LabeledSelect
            label="Tint easing"
            value={tintEase}
            onChange={(e) => setES({ tintEase: e.target.value })}
          >
            {[
              "linear","easeInQuad","easeOutQuad","easeInOutQuad",
              "easeInCubic","easeOutCubic","easeInOutCubic",
              "easeInQuart","easeOutQuart","easeInOutQuart",
              "easeInQuint","easeOutQuint","easeInOutQuint",
              "easeInSine","easeOutSine","easeInOutSine",
              "easeInExpo","easeOutExpo","easeInOutExpo",
              "easeInCirc","easeOutCirc","easeInOutCirc",
            ].map(k => <option key={k} value={k}>{k}</option>)}
          </LabeledSelect>
        </div>

        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #334155" }}>
          <div style={sectionHeader}>Trails</div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={!!trailEnabled}
              onChange={(e) => setES({ trailEnabled: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Enable trails</span>
          </label>

          <LabeledSelect
            label="Trail mode"
            value={trailMode}
            onChange={(e) => setES({ trailMode: e.target.value })}
          >
            <option value="ghost">Ghost sprites</option>
            <option value="ribbon">Ribbon (streak)</option>
            <option value="subEmitter">Sub-emitter (sparks/smoke)</option>
          </LabeledSelect>

          <div style={rowStyle}>
            <Slider
              label="Length"
              value={trailLength}
              onChange={(v) => setES({ trailLength: Math.round(clamp(v, 1, 64)) })}
              min={1}
              max={64}
              step={1}
            />
            <Slider
              label="Spacing"
              value={trailSpacingMs}
              onChange={(v) => setES({ trailSpacingMs: Math.round(clamp(v, 8, 250)) })}
              min={8}
              max={250}
              step={1}
              suffix="ms"
            />
          </div>

          <div style={rowStyle}>
            <Slider
              label="Trail alpha start"
              value={trailAlphaStart}
              onChange={(v) => setES({ trailAlphaStart: clamp(v, 0, 1) })}
              min={0}
              max={1}
              step={0.01}
            />
            <Slider
              label="Trail alpha end"
              value={trailAlphaEnd}
              onChange={(v) => setES({ trailAlphaEnd: clamp(v, 0, 1) })}
              min={0}
              max={1}
              step={0.01}
            />
          </div>

          <div style={rowStyle}>
            <Slider
              label="Trail scale start"
              value={trailScaleStart}
              onChange={(v) => setES({ trailScaleStart: Math.max(0, v) })}
              min={0}
              max={5}
              step={0.05}
              suffix="×"
            />
            <Slider
              label="Trail scale end"
              value={trailScaleEnd}
              onChange={(v) => setES({ trailScaleEnd: Math.max(0, v) })}
              min={0}
              max={5}
              step={0.05}
              suffix="×"
            />
          </div>

          {trailMode === "ribbon" && (
            <Slider
              label="Ribbon width"
              value={ribbonWidthPx}
              onChange={(v) => setES({ ribbonWidthPx: Math.round(clamp(v, 2, 128)) })}
              min={2}
              max={128}
              step={1}
              suffix="px"
            />
          )}

          {trailMode === "subEmitter" && (
            <>
              <div style={rowStyle}>
                <Slider
                  label="Trail pps"
                  value={subTrailPps}
                  onChange={(v) => setES({ subTrailPps: Math.round(clamp(v, 0, 2000)) })}
                  min={0}
                  max={2000}
                  step={1}
                />
                <Slider
                  label="Trail life"
                  value={subTrailLifeS}
                  onChange={(v) => setES({ subTrailLifeS: clamp(v, 0.05, 5) })}
                  min={0.05}
                  max={5}
                  step={0.05}
                  suffix="s"
                />
              </div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>
                Sub-emitter trails spawn a small particle system along the path (use ADD/SCREEN for glow, tinted dark for smoke).
              </div>
            </>
          )}
        </div>
      </div>
    </CollapsibleBox>
  );
}

export { ParticlesPanel };
